import { useIntl } from '@/intl'

import CheckoutSection, { Description } from '@/scopes/checkout/components/CheckoutSection'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { CarOption } from '@/scopes/checkout/components/services/CarRentalFilteringService/CarOption'
import { CarsListView } from '@/scopes/checkout/components/services/CarRentalFilteringService/CarsListView'

import { ExtraCarsView } from '@/scopes/checkout/components/services/CarRentalFilteringService/ExtraCarsView'
import {
  getFirstCarIncluded,
  getThePriceDifferenceToUpgradeIncludedCar,
  isAnyCarOptionSelectedAndIncluded,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors'
import {
  carIsBeingSelected,
  carIsBeingUpgraded,
  carToBeSelected,
  useCarRentalServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { UpgradeCarsListView } from '@/scopes/checkout/components/services/CarRentalFilteringService/UpgradeCarsListView'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useGetAllCarsQuery } from '@/scopes/checkout/filterableCars/domain/hooks'
import {
  space,
  tok,
  tokPx,
} from '@sunwebgroup/ui-components'
import { Banner, Icon, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import styled from 'styled-components'

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')};
`

const TextWrapper = styled.div`
  display: flex;
  padding-top: ${tokPx('primitives_space_2')};
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;
  flex-direction: column;
  text-wrap: auto;
`

const ItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokPx('primitives_space_4')};
  align-self: stretch;
`

const IconWrapper = styled(Icon)`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${tok('primitives_color_green_500')};
`

const CarsListsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')}
`

const CarSelectedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`

const WarningMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`

export function CarRentalFilteringService() {
  const intl = useIntl()
  const { service } = useService(ServiceType.CarRentalFiltering)

  useSendAncillaryAvailable(service, 'selectCarRental')
  const isCarBeingUpgraded = useCarRentalServiceStore(carIsBeingUpgraded)
  const isCarBeingSelected = useCarRentalServiceStore(carIsBeingSelected)
  const selectingCar = useCarRentalServiceStore(carToBeSelected)

  if (!service)
    return null
  const { usps, carLists, options } = service
  const selectingCarOption = selectingCar ? options?.[selectingCar] : null

  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_description' })}
      glyph={getIconForService(ServiceType.CarRentalFiltering)}
      moreInformationTitle=""
      moreInformationDescription=""
    >
      <MainContent>
        <Description>
          {usps?.map(({ link, text }) => (
            <ItemWrapper key={text}>
              <IconWrapper glyph="CheckMarkSmall" aria-hidden />
              <TextWrapper>
                <Typography $variant="body-m-regular-tight">
                  {link && <a href={link}>{text}</a>}
                  {!link && text}
                </Typography>
              </TextWrapper>
            </ItemWrapper>
          ))}
        </Description>

        <WarningMessages />

        {/* WHEN CAR IS ALREADY SELECTED AND DRIVER */}
        {carLists && carLists?.length > 0 && (
          <CarsListsWrapper>

            <UpgradeMessages />

            { carLists?.map(({ selectedId, selectedValue }: { selectedId?: string, selectedValue: string }, index) => {
              const optionId = selectedId || selectedValue
              const selectedOption = options?.[optionId]
              if (!selectedOption)
                return null
              return (
                <CarSelectedWrapper key={optionId}>
                  { service.isOpenJawPackage && selectedOption.openJawFeeAmount > 0 && <OpenJawMessage openJawFeeAmount={selectedOption.openJawFeeAmount} /> }
                  <CarOption isSelected {...selectedOption} />
                  {service.maxNumbersOfCarsToBeBooked > carLists?.length && (carLists?.length === index + 1) && <ExtraCarsView {...service} />}
                </CarSelectedWrapper>
              )
            })}
          </CarsListsWrapper>
        )}

        {/* First selection made */}
        { isCarBeingSelected && selectingCarOption && (
          <CarOption isSelected {...selectingCarOption} openDriverByDefault={isCarBeingSelected} />
        )}

        {/* Initial state */}
        {!selectingCarOption && carLists && carLists?.length === 0 && <CarsListView />}

        <UpgradeCarsListView
          open={isCarBeingUpgraded}
        />
      </MainContent>

    </CheckoutSection>
  )
}

function UpgradeMessages() {
  const intl = useIntl()
  const { service } = useService(ServiceType.CarRentalFiltering)
  const { data } = useGetAllCarsQuery()

  if (!service)
    return null

  const { options } = service

  const isAnyCarIncluded = isAnyCarOptionSelectedAndIncluded(Object.values(options || {}))
  const carIncluded = getFirstCarIncluded(Object.values(options || {}))
  const filterableCars = data?.cars ?? {}

  const priceToUpgrade = isAnyCarIncluded ? getThePriceDifferenceToUpgradeIncludedCar(Object.values(filterableCars ?? {}), carIncluded) : 0

  if (!isAnyCarIncluded || !priceToUpgrade)
    return null

  return (
    <Banner
      color="secondary"
      size="M"
      hasIcon
      icon="ArrowUpInCircle"
      text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_upgrade_banner_text' }, { price: intl.formatCurrency(priceToUpgrade ?? 0) })}
      supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_upgrade_banner_supporting_text' })}
    />
  )
}

function WarningMessages() {
  const intl = useIntl()
  const { service } = useService(ServiceType.CarRentalFiltering)

  if (!service)
    return null

  if (!service.includedCarOutOfStock
    && !service.includedCarPriceChanged) { return null }

  return (
    <WarningMessageWrapper>
      { service.includedCarOutOfStock && (
        <Banner
          color="warning"
          size="M"
          text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_not_available_title' })}
          supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_not_available_description' })}
        />
      )}
      { service.includedCarPriceChanged && (
        <Banner
          color="warning"
          size="M"
          text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_price_changed_title' })}
          supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_included_car_price_changed_description' })}
        />
      )}
    </WarningMessageWrapper>
  )
}

function OpenJawMessage({ openJawFeeAmount }: { openJawFeeAmount: number }) {
  const intl = useIntl()
  return (
    <Banner
      color="secondary"
      size="M"
      text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_open_jaw_car_title' })}
      supportingText={intl.formatMessage(
        { id: 'checkout_steps_ancillaries_car_rental_open_jaw_car_description' },
        { openJawFeeAmount: intl.formatCurrency(openJawFeeAmount) },
      )}
    />
  )
}
